<template>
    <b-modal 
        @hide="onHide" 
        id="send-message-modal" 
        size="lg"
        centered
        hide-header 
        hide-footer>
            <div class="message-modal">
                <button
                    @click="$bvModal.hide('send-message-modal')"
                    aria-label="Close"
                    id="modal-close"
                    class="close-modal pr-2">
                        <Icon icon="close-modal" />
                </button>
                <div v-if="partnerShop" class="message-modal__header">
                    <Avatar :src="partnerShop.logo" size="sm"/>
                    <p class="message-modal__title">
                        {{partnerShop.title | truncate(100)}}
                    </p>
                </div>
                <div class="message-modal__body">
                    <DropdownShop 
                        title="Pick Your Shop*" 
                        :data="myShopsNames"
                        @checkedShop="checkedShop = $event"
                    />
                    <div class="d-flex">
                        <p :class="{'fail': emptyText}" class="message-modal__textarea__title">Your Message*</p>
                        <p v-if="!buttonDisabled" class="ml-auto message-modal__textarea__title">
                            {{ `${0 + messageText.length}/${messageMaxLength}` }}
                        </p>
                    </div>
                    <textarea 
                        class="message-modal__textarea" 
                        :class="{'fail': emptyText}"
                        name="message" 
                        id="message-text"
                        :maxlength="messageMaxLength"
                        v-model="messageText"
                        @keyup.enter="sendMessage"
                    />
                </div>
                <div class="message-modal__footer">
                    <Button 
                        data-type="decline" 
                        @click.native="onHide"
                    >
                        Cancel
                    </Button>
                    <Button 
                        :disabled="sending" 
                        data-type="primary" 
                        @click.native="sendMessage"
                        :class="{'disabled': buttonDisabled }"
                    >
                        <span v-if="!sending">
                            Send Message
                        </span>
                        <span v-else>
                            Sending...
                        </span>
                    </Button>
                </div>
            </div>
    </b-modal>
</template>

<script>
import Avatar from '../components/Ecommerce/Avatar'
import Icon from '../components/Theme/Icon'
import DropdownShop from '../components/Ecommerce/DropdownShop'
import Button from '@/components/Theme/Button'
import { useRoom, useRouter, useStore } from '../core/hooks'
import { computed, ref, watch, onMounted } from '@vue/composition-api'
import { chatUseCase, shopsUseCase } from '../core/usecases'
import { message } from 'ant-design-vue'
import { truncate } from '../core/filters'

export default {
    name: 'SendMessageModal',
    components: {
        Avatar,
        DropdownShop,
        Button,
        Icon
    },
    setup(props, {root}) {
        const { state, store } = useStore()
        const { route, router } = useRouter()
        const messageText = ref(null)
        const emptyText = ref(false)
        const sending = ref(false)
        const checkedShop = ref('')
        const messageMaxLength = ref(800)
        const buttonDisabled = ref(false)
        const partnerShop = ref(state.value.modals['send-message-modal'].shopData)
        const myShops = computed(() => state.value.myShops)
        const myShopsNames = computed(() => myShops.value.map(el => el.title))
        const selectedShop = computed(() => myShops.value.filter(el => el.title === checkedShop.value)[0])
        const user = computed(() => store.state.user)

        watch(messageText, (messageText) => {
            if(!messageText || !messageText.trim().length) {
                return buttonDisabled.value = true
            }
            
            return buttonDisabled.value = false
        }, {immediate: true})

        onMounted(async () => {

            root.$bvModal.show('send-message-modal')

            const openedFrom = state.value.modals['send-message-modal'].openedFrom

            root.$sendMixpanelEvent('Send Message Modal Opened', {
                'Opened From': openedFrom,
                'Shop': partnerShop.value.title
            })
            root.$ga.event('Shop', 'Send Message Modal Opened', `
                OpenedFrom: ${openedFrom},
                Shop: ${partnerShop.value.title}
            `)
                
        })

        const onHide = () => {
            store.commit('TOGGLE_MODAL', ['send-message-modal', null])
            emptyText.value = false
            sending.value = false
        }


        const sendEvents =  () => {
            root.$sendMixpanelEvent('Send Message', {
                'Sender User': user.value.email,
                'Sender Shop': selectedShop.value.title,
                'Receiver Shop': partnerShop.value.title,
                'Message Box Location': 'Send Message Modal'
            });

            root.$ga.event('Chat', 'Send Message', `
                SenderUser: ${user.value.email},
                'SenderShop': ${selectedShop.value.title},
                'ReceiverShop': ${partnerShop.value.title},
                Location: Send Message Modal
            `);

            root.$incrementMixpanelPeopleProperty('Sent Messages')
        }
        
        const sendMessage = async () => {
            if(!messageText.value || !messageText.value.trim().length) {
                return 
            }
            sending.value = true
            
            sendEvents()

            message.success('The message is successfully sent.', 1, async () => {
                const roomId = await useRoom([partnerShop.value.id, selectedShop.value.id])
                if(!roomId.value) {
                    return
                }
                chatUseCase.sendMessage({
                    roomId: roomId.value,
                    partnerId: partnerShop.value.owner_id,
                    message: messageText.value.trim(),
                    isShop: true
                })
            })
            onHide()
        }

        return {
            messageText,
            emptyText,
            sending,
            myShopsNames,
            checkedShop,
            onHide,
            sendMessage,
            partnerShop,
            buttonDisabled,
            messageMaxLength
        }
    },
    filters: {
        truncate
    }
}
</script>

<style lang="scss" scoped>
$width: 592px;
$height: 487px;
$white: #ffffff;
$err_color: #FA5C7C;
$border_color: #CBD5E0;
$border: 1px solid $border_color;
$radius: 12px;
$modal_padding: 32px 56px;
$font_size: 14px;
.message-modal {
    width: $width;
    min-height: $height;
    border-radius: $radius;
    background: white;
    padding: $modal_padding;
    position: relative;
    margin: 0 auto;

    button.close-modal {
        position: absolute;
        right: 0;
        top: 0;
        transform: translate(-10px, 10px);
    }
    &__header {
        display: flex;
        flex-direction: column;
        align-items: center;
    }
    &__title {
        margin-top: 16px;
        font-size: 18px;
        line-height: 24px;
        font-weight: bold;
        margin-bottom: 0;
        text-align: center;
    }
    &__body {
        margin-top: 24px;
    }
    &__textarea {
        width: 100%;
        min-height: 120px;
        border-radius: 4px;
        border: $border;
        resize: vertical !important;
        padding: 16px;
        font-size: $font_size;
        &.fail {
            border: 1px solid #FA5C7C;
        }
    }
    &__textarea__title {
        margin-bottom: 8px;
        margin-left: 8px;
        margin-top: 16px;
        &.fail {
            color: $err_color;
        }
    }
    &__footer {
        margin-top: 16px;
        display: flex;
        justify-content: flex-end;
        button {
            padding: 8px 20px !important;
            font-size: $font_size;
            font-weight: bold;
            &:first-child {
                margin-right: 12px !important;
            }
            &.disabled[data-type="primary"] {
                background: #718096 !important;
                color: #ffffff !important;
                &:hover {
                    box-shadow: none !important;
                }
            }
        }
    }

}
</style>>

